import { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "pwa_banner_dismissed_at";
const DISMISS_DAYS = 14;

function isDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000;
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/** Mobile-only bottom banner prompting users to install the PWA.
 *  Android/Chrome: native install prompt via beforeinstallprompt.
 *  iOS Safari: Share → Add to Home Screen instructions. */
export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    if (isIOS()) {
      setIos(true);
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-[#0b1020] border-t border-white/10 px-4 py-3 shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
      data-testid="install-app-banner"
    >
      <div className="flex items-center gap-3">
        <img src="/logo192.png" alt="TradeInsights" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-white">Install TradeInsights</div>
          {ios ? (
            <div className="text-[11px] text-gray-400 flex items-center gap-1 flex-wrap">
              Tap <Share className="w-3 h-3 inline text-[#00E0FF]" /> Share, then
              <PlusSquare className="w-3 h-3 inline text-[#00E0FF]" /> Add to Home Screen
            </div>
          ) : (
            <div className="text-[11px] text-gray-400">Live signals, one tap from your home screen</div>
          )}
        </div>
        {!ios && (
          <Button
            size="sm"
            onClick={install}
            className="bg-[#00FFA3] text-black font-bold hover:bg-[#00E0FF] shrink-0"
            data-testid="install-app-button"
          >
            <Download className="w-4 h-4 mr-1" /> Install
          </Button>
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss install banner"
          className="text-gray-500 hover:text-white p-1 shrink-0"
          data-testid="install-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
